<script setup lang="ts">
import type { AccountListItem } from '#shared/types/admin'
import { useFuse } from '@vueuse/integrations/useFuse'

const props = defineProps<{
  modelValue: boolean
}>()

const emit = defineEmits<{
  'update:modelValue': [value: boolean]
  'select': [account: AccountListItem]
}>()

const { $bff } = useNuxtApp()
const { isAdmin } = useAdminAuth()
const { recordUsage, sortByRanking, hasUsageHistory } = useAccountRanking<AccountListItem>()

const isOpen = computed({
  get: () => props.modelValue,
  set: value => emit('update:modelValue', value)
})

const searchQuery = ref('')
const searchInput = useTemplateRef<{ $el: HTMLElement }>('searchInput')

// Staff (isAdmin) lists ALL accounts; regular users only the ones they can access.
const accountsEndpoint = computed(() => isAdmin.value ? '/account/list/' : '/account/list/user/')

// Fetch all accounts once - use immediate: false so we control when it fetches
const { data: allAccounts, status, error, refresh, execute } = useLazyAsyncData(
  'accounts-list',
  () => $bff<AccountListItem[]>(accountsEndpoint.value),
  {
    immediate: false,
    default: () => [],
    watch: [accountsEndpoint]
  }
)

// Sort accounts by usage ranking when no search query
const rankedAccounts = computed(() => {
  if (!allAccounts.value) return []
  return sortByRanking(allAccounts.value)
})

// Fuzzy search with Fuse.js - use ranked accounts as source
const { results: fuseResults } = useFuse(searchQuery, rankedAccounts, {
  fuseOptions: {
    keys: ['name', 'email'],
    threshold: 0.3,
    includeScore: true
  },
  matchAllWhenSearchEmpty: true
})

// Extract items from fuse results (preserves ranking when no search)
const filteredAccounts = computed(() => {
  return fuseResults.value.map(r => r.item)
})

const isLoading = computed(() => status.value === 'pending')

function selectAccount(account: AccountListItem) {
  // Record usage for ranking
  recordUsage(account.entity_uuid)
  emit('select', account)
  isOpen.value = false
}

// Focus search input and fetch data when modal opens
watch(isOpen, (open) => {
  if (open) {
    // Fetch accounts when modal opens (only if not already loaded)
    if (!allAccounts.value?.length) {
      execute()
    }
    // Focus the input
    nextTick(() => {
      const input = searchInput.value?.$el?.querySelector('input')
      input?.focus()
    })
  } else {
    searchQuery.value = ''
  }
})
</script>

<template>
  <UModal
    v-model:open="isOpen"
    title="Select Account"
    :description="isAdmin ? 'Choose a merchant account to impersonate' : 'Choose an account to view'"
    :ui="{
      content: 'sm:max-w-xl'
    }"
  >
    <template #body>
      <div class="space-y-4">
        <!-- Search Input -->
        <UInput
          ref="searchInput"
          v-model="searchQuery"
          placeholder="Search by name or email..."
          icon="i-lucide-search"
          size="lg"
          autofocus
        />

        <!-- Loading State -->
        <div v-if="isLoading" class="space-y-2">
          <div
            v-for="i in 5"
            :key="i"
            class="p-4 rounded-lg border border-default"
          >
            <div class="flex items-center justify-between">
              <div class="flex-1 space-y-2">
                <div class="flex items-center gap-2">
                  <USkeleton class="h-4 w-32" />
                  <USkeleton v-if="i % 3 === 0" class="h-4 w-10" />
                </div>
                <USkeleton class="h-3 w-48" />
              </div>
              <USkeleton class="h-5 w-5 rounded" />
            </div>
          </div>
        </div>

        <!-- Error State -->
        <div v-else-if="error" class="text-center py-8">
          <UIcon name="i-lucide-alert-circle" class="w-10 h-10 text-red-400 mx-auto mb-2" />
          <p class="text-red-400 mb-4">
            Failed to load accounts
          </p>
          <UButton variant="outline" @click="refresh()">
            Retry
          </UButton>
        </div>

        <!-- Empty State -->
        <div v-else-if="filteredAccounts.length === 0" class="text-center py-8">
          <UIcon name="i-lucide-users" class="w-10 h-10 text-muted mx-auto mb-2" />
          <p class="text-muted">
            {{ searchQuery ? 'No accounts match your search' : 'No accounts found' }}
          </p>
        </div>

        <!-- Account List -->
        <div v-else class="space-y-2 max-h-96 overflow-y-auto">
          <button
            v-for="account in filteredAccounts"
            :key="account.entity_uuid"
            class="w-full text-left p-4 rounded-lg border border-default hover:bg-elevated/50 hover:border-primary/30 transition-all group cursor-pointer"
            @click="selectAccount(account)"
          >
            <div class="flex items-center justify-between">
              <div class="min-w-0">
                <div class="flex items-center gap-2">
                  <UIcon
                    v-if="hasUsageHistory(account.entity_uuid)"
                    name="i-lucide-clock"
                    class="w-3.5 h-3.5 text-muted shrink-0"
                    title="Recently used"
                  />
                  <span class="font-medium text-default truncate">
                    {{ account.name }}
                  </span>
                  <UBadge
                    v-if="account.is_test_account"
                    color="warning"
                    variant="subtle"
                    size="xs"
                  >
                    Test
                  </UBadge>
                </div>
                <p class="text-sm text-muted truncate">
                  {{ account.email }}
                </p>
              </div>
              <UIcon
                name="i-lucide-chevron-right"
                class="w-5 h-5 text-muted group-hover:text-primary transition-colors"
              />
            </div>
          </button>
        </div>

        <!-- Account Count -->
        <div v-if="filteredAccounts.length > 0" class="text-xs text-muted text-center pt-2">
          <template v-if="searchQuery">
            Showing {{ filteredAccounts.length }} of {{ allAccounts?.length }} accounts
          </template>
          <template v-else>
            {{ filteredAccounts.length }} account{{ filteredAccounts.length === 1 ? '' : 's' }}
          </template>
        </div>
      </div>
    </template>
  </UModal>
</template>
